// pages/User/UserPage.jsx
import { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import Layout from '@/components/Layout/Layout.jsx';
import ProductService from '../../services/ProductService';
import UserService from '../../services/UserServices';
import UserHeader from './components/UserHeader';
import UserStats from './components/UserStats';
import UserAdsList from './components/UserAdsList';
import { useTranslation } from 'react-i18next';
import SEO from '@/components/SEO/SEO.jsx';

const UserPage = () => {
  const { userId } = useParams();
  const { t } = useTranslation();
  const [user, setUser] = useState(null);
  const [userAds, setUserAds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [adsLoading, setAdsLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalAds, setTotalAds] = useState(0);
  const pageSize = 12;

  const fetchUser = useCallback(async () => {
    try {
      setLoading(true);
      console.log('[UserPage] fetchUser userId:', userId);
      const response = await UserService.getPublicProfile(userId);
      console.log('[UserPage] fetchUser response:', response?.data);
      setUser(response.data);
    } catch (err) {
      console.error('Error fetching user:', err);
      err?.handleGlobally?.();
    } finally {
      setLoading(false);
    }
  }, [userId]);

  const fetchUserAds = useCallback(
    async (page = 1) => {
      try {
        setAdsLoading(true);
        // Attempt 1: PUBLIC products with ownerId filter (shows active, approved, not paused)
        const publicParams = new URLSearchParams({ page: String(page), limit: String(pageSize), ownerId: String(userId) }).toString();
        const fullPath = `products?${publicParams}`;
        let res = await ProductService.getAds(fullPath);

        // If no data, Attempt 2: pageSize without status
        if (!Array.isArray(res?.data?.data) || res.data.data.length === 0) {
          const attempt2 = `page=${page}&pageSize=${pageSize}`;
          res = await ProductService.getUserAds(userId, attempt2);
        }

        // If still no data, Attempt 3: limit (instead of pageSize) without status
        if (!Array.isArray(res?.data?.data) || res.data.data.length === 0) {
          const attempt3 = `page=${page}&limit=${pageSize}`;
          res = await ProductService.getUserAds(userId, attempt3);
        }

        // If still no data, Attempt 4: use dashboard endpoint with userId filter
        if (!Array.isArray(res?.data?.data) || res.data.data.length === 0) {
          const attempt4 = new URLSearchParams({ page: String(page), limit: String(pageSize), userId: String(userId) }).toString();
          const dashRes = await UserService.userAdsDashboard(attempt4, {});
          // Normalize to the same "res" shape for processing below
          res = dashRes;
        }

        // Apply response (even if empty, we'll reflect accurate totals)
        setUserAds(res?.data?.data ?? []);
        const totalCount = res?.data?.totalCount ?? res?.data?.count ?? res?.data?.data?.length ?? 0;
        const pages = res?.data?.totalPages ?? (totalCount ? Math.ceil(totalCount / pageSize) : 1);
        setTotalPages(pages);
        setTotalAds(res?.data?.count ?? totalCount);
        setCurrentPage(page);
      } catch (err) {
        console.error('Error fetching user ads:', err);
        err?.handleGlobally?.();
      } finally {
        setAdsLoading(false);
      }
    },
    [userId]
  );

  useEffect(() => {
    if (userId) {
      fetchUser();
      fetchUserAds(1);
    }
  }, [userId, fetchUser, fetchUserAds]);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  const handlePageChange = (newPage) => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
    fetchUserAds(newPage);
  };

  if (loading) {
    return (
      <Layout>
        <main className='flex min-h-screen w-full items-center justify-center py-12'>
          <div className='text-center'>
            <div className='mx-auto mb-4 h-16 w-16 animate-spin rounded-full border-4 border-primary-yellow border-t-transparent'></div>
            <p className='text-lg font-medium text-gray-600'>{t('loading')}...</p>
          </div>
        </main>
      </Layout>
    );
  }

  if (!user) {
    return (
      <Layout>
        <main className='flex min-h-screen w-full items-center justify-center py-12'>
          <div className='max-w-md rounded-lg bg-white p-12 text-center shadow-xl'>
            <div className='mx-auto mb-4 flex h-20 w-20 items-center justify-center rounded-full bg-red-100'>
              <svg className='h-10 w-10 text-red-500' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
                <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M6 18L18 6M6 6l12 12' />
              </svg>
            </div>
            <h2 className='mb-3 text-2xl font-bold text-gray-800'>{t('userNotFound')}</h2>
            <p className='mb-6 text-gray-600'>{t('userNotFoundDescription')}</p>
            <a
              href='/products'
              className='inline-block rounded-full bg-primary-yellow px-6 py-3 font-semibold text-gray-800 transition-colors hover:bg-yellow-500'
            >
              {t('backToAds')}
            </a>
          </div>
        </main>
      </Layout>
    );
  }

  return (
    <Layout>
      <SEO
        title={user ? `${user.firstName} ${user.lastName} – Rentasvet` : 'Korisnik – Rentasvet'}
        description={
          user
            ? `Pogledajte sve oglase korisnika ${user.firstName} ${user.lastName} na Rentasvetu. Direktna komunikacija bez posrednika i provizije.`
            : 'Korisnički profil na Rentasvetu.'
        }
        keywords={
          user
            ? `${user.firstName} ${user.lastName}, oglasi korisnika, Rentasvet, rentiranje, iznajmljivanje`
            : 'korisnik, profil, Rentasvet'
        }
        url={`/user-details/${userId}`}
        lang='sr'
      />
      <main className='min-h-screen w-full px-4 py-6 lg:px-12 lg:py-10'>
        <div className='mx-auto max-w-7xl space-y-6'>
          <UserHeader user={user} />
          <UserStats user={user} totalAds={totalAds} />
          <UserAdsList
            ads={userAds}
            loading={adsLoading}
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={handlePageChange}
            userName={`${user.firstName} ${user.lastName}`}
          />
        </div>
      </main>
    </Layout>
  );
};

export default UserPage;
